import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { AgentOrbType, ORB_BY_TEMPLATE } from '../agent-orb/agent-orb.component';
import { ORBI_IDLE, OrbiGaze } from '../orbi-lab/orbi-lab.component';
import { ChatPreviewService } from '../../services/chat-preview.service';
import { ChatService, ChatSettings, DEFAULT_CHAT_STYLES } from '../../services/chat.service';
import {
  Tutor,
  TutorService,
  TutorTemplate,
} from '../../services/tutor.service';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';

/** FNV-1a — klein, deterministisch, verteilt kurze IDs gut genug für einen
 *  PRNG-Seed. Keine Krypto-Anforderung, nur "verschiedene Agent-IDs sollen
 *  verschiedene Ausdrucks-Folgen ziehen". */
function hashSeed(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 — winziger seedbarer PRNG, genug Streuung für ein
 *  Fisher-Yates über eine Handvoll Elemente. */
function mulberry32(seed: number): () => number {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Bewegungen für die Agent-Kachel (Lab-IDs aus orbi-lab/vendor/sequences).
 *  In Kachelgröße gemessen (27.09.2026): alle bleiben in der Kachel, der große
 *  Hüpfer ragt am Scheitel ~7px über den oberen Rand. Nie auf dem Dashboard:
 *  `hop-zigzag` und `hop-right` — die springen rechts aus der Kachel, wo der
 *  Orbi in der Ecke sitzt (Marcel, 25./27.09.2026). */
const TILE_MOVES = ['laugh', 'hop-small', 'hop', 'hop-left', 'giggle-bounce', 'hop-laugh', 'spin-centrifugal'];

/** Pfeifen steht in jedem Ablauf, bei jedem zweiten Orbi doppelt (Marcel:
 *  "ich liebe das Pfeifen, gerne öfter"). */
const WHISTLE = 'idle-whistle';

/** Verstecken & Erschrecken nur "bei einigen" — etwa jedem dritten Orbi. Der
 *  Überraschungssprung am Ende ragt auf der großen Kachel ~31px über den oberen
 *  Rand (gemessen), auf der kleinen ~12px. */
const HIDE_SURPRISE = 'hide-surprise';
const HIDE_SURPRISE_SHARE = 0.35;

function shuffled<T>(list: readonly T[], random: () => number): T[] {
  const result = [...list];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** Nie zweimal dieselbe Bewegung direkt hintereinander (betrifft das doppelte
 *  Pfeifen). */
function avoidBackToBack(list: string[]): string[] {
  const result = [...list];
  for (let i = 1; i < result.length; i += 1) {
    if (result[i] !== result[i - 1]) continue;
    const j = result.findIndex((id, k) => k > i && id !== result[i - 1]);
    if (j !== -1) [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** Eigener, endlos wiederholter Ablauf je Agent (Marcel, 27.09.2026: "alle
 *  unterschiedlich, auch innerhalb einer Template-Gruppe"). Aus `tutorId`
 *  geseedet: derselbe Agent spielt nach jedem Reload denselben Ablauf, zwei
 *  Agenten praktisch nie denselben — Auswahl (2–4 aus `TILE_MOVES`),
 *  Reihenfolge, doppeltes Pfeifen, Verstecken, Pausenlängen und direkte
 *  Zweierfolgen variieren unabhängig. `ORBI_IDLE` = normal schauen (2,5s). */
function pickPersonalityScript(tutorId: string | undefined): string[] {
  const random = mulberry32(hashSeed(tutorId ?? 'orbi'));
  const moves = [WHISTLE, ...shuffled(TILE_MOVES, random).slice(0, 2 + Math.floor(random() * 3))];
  if (random() < 0.5) moves.push(WHISTLE);
  if (random() < HIDE_SURPRISE_SHARE) moves.push(HIDE_SURPRISE);

  const order = avoidBackToBack(shuffled(moves, random));
  const script: string[] = random() < 0.5 ? [ORBI_IDLE] : [];
  order.forEach((move, i) => {
    script.push(move);
    const isLast = i === order.length - 1;
    // Meist 5–7,5s normal schauen, gelegentlich zwei Bewegungen direkt
    // nacheinander (wie "Zickzack → Lachen" im Anlege-Dialog).
    if (isLast || random() >= 0.25) {
      script.push(ORBI_IDLE, ORBI_IDLE);
      if (random() < 0.5) script.push(ORBI_IDLE);
    }
  });
  return script;
}

const TILE_GAZES: OrbiGaze[] = ['left', 'right', 'center'];

/** Blickrichtung je Kachel (Marcel, 02.11.2026: "manche nach links, manche
 *  nach rechts, manche geradeaus"). Bei jedem Laden neu gezogen, nicht aus der
 *  Agent-ID (05.11.2026: "es muss mehr willkürlich sein"); `jitter` streut,
 *  wann er kurz zur Seite schaut. Die Abläufe oben bleiben geseedet. */
function pickGaze(): { gaze: OrbiGaze; jitter: number } {
  const gaze = TILE_GAZES[Math.floor(Math.random() * TILE_GAZES.length)];
  return { gaze, jitter: Math.random() };
}

@Component({
  selector: 'app-tutor-card',
  templateUrl: './tutor-card.component.html',
  styleUrl: './tutor-card.component.scss',
})
export class TutorCardComponent implements OnInit, OnChanges, OnDestroy {
  /** Ablauf des Orbis, aus `tutor.id` geseedet (siehe
   *  `pickPersonalityScript`). Bewusst ein Feld, kein Getter — Regel 18
   *  (CLAUDE.md): ein Getter würde bei jedem Change-Detection-Durchlauf ein
   *  neues Array bauen; berechnet wird hier nur, wenn sich `tutor` ändert. */
  personalityScript: string[] = [];
  /** Blickrichtung des Orbis, je Laden zufällig (`pickGaze`). */
  orbiGaze: OrbiGaze = 'scan';
  orbiGazeJitter = 0;

  @Input() tutor!: Tutor; // Replace 'any' with the actual type of tutor if available
  @Input() activeUsers: number = 0; // Default to 0 if not provided

  @Input() size: 'small' | 'large' = 'large'; // Default size

  chatSettings: ChatSettings | undefined;
  tutorTemplate: TutorTemplate | undefined;

  // Module-scoped registry of logo URLs that already failed to load
  // (broken user input like via.placeholder.com, dead CDN, etc.).
  // Shared across every tutor-card instance so 8 cards with the same
  // dead URL only fail once, and re-emissions of `chatSettings$` don't
  // re-render the <img> and re-trigger the request — that was the
  // source of the flicker / endless GET loop in the live UI.
  private static readonly failedLogoUrls = new Set<string>();

  subscriptions: any[] = [];

  constructor(
    protected chatService: ChatService,
    protected tutorService: TutorService,
    protected chatPreviewService: ChatPreviewService,
    protected nbDialogService: NbDialogService,
    protected router: Router
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.tutorService.tutorTemplates$.subscribe((templates) => {
        this.tutorTemplate = templates.find(
          (template) => template.id === this.tutor.template_id
        );
      })
    );

    this.subscriptions.push(
      this.chatService.chatSettings$.subscribe((settings) => {
        if (settings.length) {
          this.chatSettings = settings.find(
            (setting) => setting.id === this.tutor.widget_setting_id
          );
        }
      })
    );
  }

  /** Logo URL to actually render. Returns null if no logo is configured
   *  or if this URL has already failed to load in this session — the
   *  template then renders the default Eva icon directly without
   *  emitting another <img> request. */
  get effectiveLogoUrl(): string | null {
    const url = this.chatSettings?.styles?.logo;
    if (!url) return null;
    if (TutorCardComponent.failedLogoUrls.has(url)) return null;
    return url;
  }

  onLogoLoadError(): void {
    const url = this.chatSettings?.styles?.logo;
    if (url) TutorCardComponent.failedLogoUrls.add(url);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub?.unsubscribe());
  }

  ngOnChanges(changes: any): void {
    // Handle any changes to the input properties if necessary
    if (changes.tutor) {
      this.tutor = changes.tutor.currentValue;
      this.personalityScript = pickPersonalityScript(this.tutor?.id);
      // Nur beim ersten Mal — sonst wechselt er bei jeder Aktualisierung der Liste.
      if (this.orbiGaze === 'scan') ({ gaze: this.orbiGaze, jitter: this.orbiGazeJitter } = pickGaze());
    }
    if (changes.activeUsers) {
      this.activeUsers = changes.activeUsers.currentValue;
    }

    if (changes.size) {
      this.size = changes.size.currentValue;
    }
  }

  openTutorPage() {
    this.router.navigate(['/tutors', this.tutor.id, 'general']);
  }

  onDeleteTutor($event: MouseEvent) {
    $event.stopPropagation();
    const confirmDialogRef = this.nbDialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
    });

    // Delete the project if the user confirms
    confirmDialogRef.onClose.subscribe(async (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.tutorService.deleteTutor(this.tutor.id).subscribe(() => {
          this.tutorService.listTutors().subscribe(() => {});
        });
      }
    });
  }

  onDuplicateTutor($event: MouseEvent) {
    $event.stopPropagation();
    this.tutorService.duplicateTutor(this.tutor.id).subscribe(() => {
      this.tutorService.listTutors().subscribe(() => {});
    });
  }

  onStartChat(event: MouseEvent) {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.isChatActive) {
      this.chatPreviewService.closeChat();
      return;
    }

    this.chatPreviewService.connect(undefined, this.tutor.id);
  }

  get isChatActive() {
    return this.chatPreviewService.isChatActive$.value;
  }

  // Fallbacks = the default styling an agent without an assigned chat
  // style actually shows (DEFAULT_CHAT_STYLES) — the swatches on the card
  // must match what the learner sees in the widget.
  get backgroundColor() {
    return this.chatSettings?.styles?.backgroundColor ?? DEFAULT_CHAT_STYLES.backgroundColor;
  }

  get botTextColor() {
    return this.chatSettings?.styles?.botTextColor ?? DEFAULT_CHAT_STYLES.botTextColor;
  }

  get userTextColor() {
    return this.chatSettings?.styles?.userTextColor ?? DEFAULT_CHAT_STYLES.userTextColor;
  }

  get secondaryColor() {
    return this.chatSettings?.styles?.secondaryColor ?? DEFAULT_CHAT_STYLES.secondaryColor;
  }

  get primaryColor() {
    return this.chatSettings?.styles?.primaryColor ?? DEFAULT_CHAT_STYLES.primaryColor;
  }

  /** Der Orb steht oben rechts für das Agent-Template — dieselbe Zuordnung
   *  wie im Anlege-Dialog, damit die Kachel zeigt, was dort gewählt wurde.
   *  Templates ohne eigenen Orb (qa_basic & Co.) fallen wie dort auf
   *  `tutor` zurück. Solange die Templates noch laden, steht `tutorTemplate`
   *  auf undefined; der Fallback trägt das mit. */
  get orbType(): AgentOrbType {
    return ORB_BY_TEMPLATE[this.tutorTemplate?.name ?? ''] || 'tutor';
  }

  /** Kantenlänge des Orbs. Die schmale Kachel (~200px) trägt keinen 72er.
   *  Als Zahl statt per CSS, weil der Orb sein SVG selbst bemaßt. */
  get orbSize(): number {
    return this.size === 'small' ? 40 : 56;
  }

  /** Kurse, in denen der Agent live ist. Kommt fertig aus dem Listen-
   *  Endpunkt (`live_course_count`); ältere Backends liefern das Feld noch
   *  nicht, dann steht dort 0 statt einer leeren Zeile. */
  get liveCourseCount(): number {
    return this.tutor.live_course_count ?? 0;
  }
}
